import type { ClinicSettings, SettingKey } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useState } from "react";
import { errorMessage, get, patch } from "../../api";
import { Alert, Button } from "../../components/ui";

export interface SettingsResponse {
  clinic: { id: string; name: string; timezone: string; slug: string | null };
  settings: ClinicSettings;
  logoUrl: string | null;
  /** Email from an address on this domain gets replies back into Vaulted. */
  replyDomain: string | null;
}

export function useSettings() {
  return useQuery({ queryKey: ["settings"], queryFn: () => get<SettingsResponse>("/api/settings") });
}

/**
 * Local, editable copy of some settings, saved together.
 * Usage: const d = useDraft(settings, ["a", "b"]); d.value["a"]; d.set("a", v); d.save().
 */
export function useDraft<K extends SettingKey>(settings: ClinicSettings, keys: readonly K[], successMessage: string) {
  const qc = useQueryClient();
  const pick = () => Object.fromEntries(keys.map((k) => [k, settings[k]])) as Pick<ClinicSettings, K>;
  const [value, setValue] = useState(pick);
  const [notice, setNotice] = useState<string | null>(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setValue(pick()), [settings]);
  const mutation = useMutation({
    mutationFn: () => patch<SettingsResponse>("/api/settings", value),
    onSuccess: async (d) => {
      qc.setQueryData(["settings"], d);
      await qc.invalidateQueries({ queryKey: ["me"] });
      setNotice(successMessage);
    },
  });
  const set = <T extends K>(k: T, v: ClinicSettings[T]) => {
    setNotice(null);
    setValue((cur) => ({ ...cur, [k]: v }));
  };
  const dirty = JSON.stringify(value) !== JSON.stringify(pick());
  return { value, set, save: () => mutation.mutate(), saving: mutation.isPending, error: mutation.error, notice, dirty };
}

/** A settings form: status messages on top, Save at the bottom. */
export function SettingsForm({
  children,
  onSave,
  saving,
  error,
  notice,
  dirty = true,
  label = "Save",
}: {
  children: ReactNode;
  onSave: () => void;
  saving: boolean;
  error: unknown;
  notice: string | null;
  dirty?: boolean;
  label?: string;
}) {
  return (
    <form
      className="flex flex-col gap-4 px-5 py-5"
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
    >
      {children}
      {error ? <Alert>{errorMessage(error)}</Alert> : null}
      {notice && !dirty ? <Alert tone="ok">{notice}</Alert> : null}
      <div className="flex justify-end">
        <Button variant="primary" type="submit" busy={saving} disabled={!dirty}>
          {label}
        </Button>
      </div>
    </form>
  );
}

/** Small number input used inside rule tables. */
export function NumberInput({
  value,
  onChange,
  min,
  max,
  step,
  label,
  suffix,
}: {
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label: string;
  suffix?: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <input
        type="number"
        aria-label={label}
        className="w-20 rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5 text-[15px] focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
        value={Number.isFinite(value) ? value : ""}
        min={min}
        max={max}
        step={step ?? 1}
        onChange={(e) => onChange(e.target.value === "" ? NaN : Number(e.target.value))}
        required
      />
      {suffix && <span className="text-sm text-muted">{suffix}</span>}
    </span>
  );
}

export function RemoveButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} className="rounded-md p-1.5 text-muted hover:bg-mist hover:text-danger" aria-label={label}>
      <svg viewBox="0 0 20 20" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.8">
        <path d="M5 5l10 10M15 5L5 15" />
      </svg>
    </button>
  );
}

export function AddButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="self-start text-sm font-semibold text-brand hover:text-brand-deep">
      + {children}
    </button>
  );
}
